import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Plus,
  ChevronRight,
  CloudRain,
  Wind,
  MapPin,
  Navigation,
  AlertTriangle,
  RefreshCw,
  Boxes,
} from 'lucide-react';
import {
  KpiChip,
  FreeSlotChip,
  EmptyState,
  StatusPill,
} from '../../components/ui';
import { useWeather, getConditionEmoji } from '../../components/WeatherWidget';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import TrialBanner from './TrialBanner';
import SetCredentialsBanner from './SetCredentialsBanner';
import QuickActions from './QuickActions';
import {
  getBookingCalendarDateStr,
  parseBookingTimeRange,
  formatHm,
  formatInr,
  getLiveScheduleStatus,
} from './homeDashboardUtils';

function paymentKeyFor(booking) {
  const total = parseFloat(booking.totalAmount) || parseFloat(booking.price) || 0;
  const advance = parseFloat(booking.advanceAmount) || 0;
  if (total > 0 && total - advance <= 0) return 'paid';
  if (advance > 0) return 'partial';
  return 'pending';
}

function bookingTitle(booking) {
  const a = booking.teamA?.name;
  const b = booking.teamB?.name;
  if (a && b) return `${a} vs ${b}`;
  return booking.bookedBy || booking.teamA?.captainName || booking.contactName || a || 'Guest';
}

function bookingTimeRange(booking) {
  const tr = parseBookingTimeRange(booking);
  if (tr) return `${formatHm(tr.startH, tr.startM)}–${formatHm(tr.endH, tr.endM)}`;
  return `${booking.timeSlot || ''}`.trim() || null;
}

function CompactBookingRow({ timeLine, title, amount, statusKey, statusLabel, paymentNote }) {
  const isCompleted = String(statusKey).toLowerCase() === 'completed';
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ga-border-subtle px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        {timeLine && (
          <p className="truncate text-[12px] text-ga-text-muted">{timeLine}</p>
        )}
        <p className="mt-0.5 truncate text-sm font-semibold text-ga-text-primary">{title}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold text-ga-text-primary">{amount}</p>
        {paymentNote && (
          <p className="mt-0.5 text-[11px] font-medium text-ga-success">{paymentNote}</p>
        )}
        <div className="mt-1 flex justify-end">
          {isCompleted ? (
            <span className="inline-flex rounded-ga-full bg-ga-text-primary px-2 py-0.5 text-[11px] font-semibold text-ga-text-on-primary">
              {statusLabel || 'Completed'}
            </span>
          ) : (
            <StatusPill status={statusKey} label={statusLabel} size="sm" dot={false} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Weather card — live conditions when available; GPS prompt when location is missing. */
function WeatherCard({ facility, bookingCountByDate }) {
  const { weather, status, refresh } = useWeather(facility);
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MODULES.CONFIGURATION, 'write');
  const [showForecast, setShowForecast] = useState(false);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface px-4 py-3 shadow-ga-soft text-ga-text-muted">
        <RefreshCw className="h-4 w-4 animate-spin text-ga-primary" aria-hidden />
        <span className="text-sm">Loading weather…</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mt-4 flex items-center justify-between gap-3 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface px-4 py-3 shadow-ga-soft">
        <div className="flex items-center gap-2 text-ga-text-muted">
          <CloudRain className="h-4 w-4 shrink-0" aria-hidden />
          <span className="text-sm">Weather unavailable</span>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-[13px] font-semibold text-ga-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (status === 'no-location') {
    return (
      <div className="mt-4 overflow-hidden rounded-ga-md border border-ga-warning/30 bg-ga-warning/10 shadow-ga-soft">
        <div className="flex items-start gap-3 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-ga-warning" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ga-text-primary">Ground location not set</p>
            <p className="mt-0.5 text-[12px] text-ga-text-muted">
              Add GPS coordinates to show live weather and rain alerts
              {facility?.name ? ` for ${facility.name}` : ''}.
            </p>
            {canManage ? (
              <Link
                to="/dashboard/facility-location"
                className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ga-primary"
              >
                <Navigation className="h-3.5 w-3.5" aria-hidden />
                Set ground location
              </Link>
            ) : (
              <p className="mt-2 text-[12px] text-ga-text-muted">
                Ask an admin to set the ground location.
              </p>
            )}
          </div>
          <MapPin className="h-5 w-5 shrink-0 text-ga-warning/70" aria-hidden />
        </div>
      </div>
    );
  }

  if (!weather?.current) return null;

  const { current, forecast, location } = weather;
  const rainHit = (forecast || [])
    .map((day, idx) => {
      const dateKey = String(day.date).slice(0, 10);
      return {
        date: dateKey,
        rain: day.chance_of_rain ?? 0,
        bookings: bookingCountByDate?.[dateKey] || 0,
        idx,
      };
    })
    .find((d) => d.rain >= 50 && d.bookings > 0);

  let rainLabel = null;
  if (rainHit) {
    const d = parseISO(rainHit.date);
    const when =
      rainHit.idx === 0
        ? 'today'
        : rainHit.idx === 1
          ? 'tomorrow'
          : Number.isNaN(d.getTime())
            ? rainHit.date
            : format(d, 'EEE');
    rainLabel = {
      title: `Rain expected ${when} · ${rainHit.rain}%`,
      body: 'Night slot booking at risk. Plan ahead.',
    };
  }

  const place =
    facility?.name ||
    (location?.name ? `${location.name}${location.region ? `, ${location.region}` : ''}` : null);

  return (
    <div className="mt-4 overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-2xl leading-none" aria-hidden>
          {getConditionEmoji(current.condition?.code)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ga-text-primary">
            {Math.round(current.temp_c)}° · {current.condition?.text}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ga-text-muted">
            {current.wind_kph != null && (
              <span className="inline-flex items-center gap-1">
                <Wind className="h-3 w-3" aria-hidden />
                {Math.round(current.wind_kph)} km/h
              </span>
            )}
            {place && <span className="truncate">{place}</span>}
          </div>
        </div>
        {(forecast?.length > 0) && (
          <button
            type="button"
            onClick={() => setShowForecast((v) => !v)}
            className="shrink-0 text-[12px] font-medium text-ga-primary"
          >
            7-day →
          </button>
        )}
      </div>

      {showForecast && forecast?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto border-t border-ga-border-subtle px-4 py-2">
          {forecast.slice(0, 7).map((day) => (
            <div
              key={day.date}
              className="min-w-[52px] shrink-0 rounded-ga-sm bg-ga-bg-page px-2 py-1.5 text-center"
            >
              <p className="text-[10px] text-ga-text-muted">
                {format(parseISO(String(day.date).slice(0, 10)), 'EEE')}
              </p>
              <p className="text-xs font-semibold text-ga-text-primary">
                {Math.round(day.avg_c ?? day.max_c ?? 0)}°
              </p>
              <p className="text-[10px] text-ga-info">{day.chance_of_rain ?? 0}%</p>
            </div>
          ))}
        </div>
      )}

      {rainLabel && (
        <div className="flex items-center gap-3 bg-[#1c4e86] px-4 py-3">
          <CloudRain className="h-6 w-6 shrink-0 text-[#bcd8f5]" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-bold text-white">{rainLabel.title}</p>
            <p className="mt-0.5 text-[11px] text-[#bcd8f5]">{rainLabel.body}</p>
          </div>
          <Link
            to="/dashboard/my-bookings"
            className="shrink-0 rounded-ga-full bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1c4e86]"
          >
            Notify
          </Link>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, trailing, to, trailingLink }) {
  return (
    <div className="mb-2 mt-5 flex items-center justify-between gap-2">
      <h2 className="text-base font-bold text-ga-text-primary">{title}</h2>
      {to ? (
        <Link to={to} className="inline-flex items-center gap-0.5 text-[12px] font-medium text-ga-primary">
          {trailingLink || trailing || 'View all'} <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      ) : (
        trailing && <span className="text-[12px] text-ga-text-muted">{trailing}</span>
      )}
    </div>
  );
}

export default function HomeMobile(props) {
  const {
    facility,
    today,
    occupancyPercentage,
    bookingsThisWeek,
    bookingsLastWeek,
    revenueThisMonth,
    paymentsSummary = {},
    todaysBookings = [],
    upcomingBookings = [],
    bookingCountByDate,
    pendingRequestCount = 0,
    operationsSummary = {},
    inventoryLowStockCount = 0,
    inventoryTotalItems = 0,
    dynamicInsights = {},
    lastSyncedAt,
    summaryError,
    onRefresh,
    refreshBusy = false,
  } = props;

  const navigate = useNavigate();
  const goBookNow = () => navigate('/dashboard/book-now');
  const now = today instanceof Date ? today : new Date();

  const collectedToday = paymentsSummary.collectedToday ?? 0;
  const pendingCount = paymentsSummary.pendingCount ?? 0;
  const pendingAmount = paymentsSummary.pendingAmount ?? 0;
  const weekDelta = (bookingsThisWeek || 0) - (bookingsLastWeek || 0);
  const opsTotal = operationsSummary.totalTasks ?? 0;
  const opsDone = operationsSummary.completedCount ?? 0;

  const futureUpcoming = useMemo(
    () => upcomingBookings.slice(0, 4),
    [upcomingBookings]
  );

  const hasAlerts = pendingCount > 0 || opsTotal > 0 || inventoryLowStockCount > 0;
  const hasInventory = inventoryTotalItems > 0;

  // Peak hours: prefer compact "HH–HH" from dynamicInsights when possible
  const peakHours = useMemo(() => {
    const raw = dynamicInsights.peakBookingHours || '–';
    if (raw === '–') return raw;
    // "Sat 18:00 – 22:00" → "18–22"
    const m = String(raw).match(/(\d{1,2}):\d{2}\s*[–-]\s*(\d{1,2}):\d{2}/);
    if (m) return `${m[1]}–${m[2]}`;
    return raw;
  }, [dynamicInsights.peakBookingHours]);

  return (
    <div className="pb-2">
      {/* Title row — no FAB; New booking lives in quick actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[26px] font-bold leading-tight text-ga-text-primary">Home</h1>
          <p className="mt-0.5 truncate text-xs text-ga-text-muted">
            {facility?.name && <span>{facility.name} · </span>}
            {format(now, 'EEE, MMM d')}
          </p>
        </div>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshBusy}
            className="ga-btn-ghost inline-flex shrink-0 items-center gap-1.5 px-2 py-1.5 text-xs disabled:opacity-60"
            aria-label="Refresh app data"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshBusy ? 'animate-spin' : ''}`} aria-hidden />
            <span className="font-medium">Refresh</span>
          </button>
        )}
      </div>
      {lastSyncedAt != null && (
        <p className={`mt-1 text-[11px] ${summaryError ? 'text-ga-danger' : 'text-ga-text-subtle'}`}>
          {refreshBusy
            ? 'Syncing…'
            : summaryError
              ? "Couldn't refresh — showing last data"
              : `Synced ${Math.max(0, Math.floor((Date.now() - lastSyncedAt.getTime()) / 60000))} min ago`}
        </p>
      )}

      <div className="mt-3 space-y-3">
        <TrialBanner />
        <SetCredentialsBanner />
      </div>

      <div className="mt-3">
        <QuickActions pendingRequestCount={pendingRequestCount} scrollable />
      </div>

      {/* KPI row — 2×2 matching mockup density */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <KpiChip label="Occupancy" value={`${occupancyPercentage}%`} accent="primary" />
        <KpiChip
          label="Revenue"
          value={formatInr(revenueThisMonth)}
          accent="success"
          delta={
            collectedToday > 0
              ? { value: `+${formatInr(collectedToday)} today`, direction: 'up' }
              : undefined
          }
        />
        <KpiChip
          label="This week"
          value={bookingsThisWeek || 0}
          accent="info"
          delta={
            weekDelta > 0
              ? { value: '↑', direction: 'up' }
              : weekDelta < 0
                ? { value: '↓', direction: 'down' }
                : undefined
          }
        />
        <KpiChip
          label="Pending"
          value={pendingCount}
          accent="warning"
          className={pendingCount > 0 ? 'border-ga-warning/40' : undefined}
          onClick={() => navigate('/dashboard/payments')}
        />
      </div>

      <WeatherCard facility={facility} bookingCountByDate={bookingCountByDate} />

      {/* Today's schedule — compact booked rows */}
      <SectionHeader
        title="Today's schedule"
        trailing={`${todaysBookings.length} booked`}
      />
      {todaysBookings.length > 0 ? (
        <div className="overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft">
          {todaysBookings.map((booking, index) => {
            const pk = paymentKeyFor(booking);
            const live = getLiveScheduleStatus(booking);
            const range = bookingTimeRange(booking);
            return (
              <CompactBookingRow
                key={booking.id || index}
                timeLine={range}
                title={bookingTitle(booking)}
                amount={formatInr(booking.totalAmount || booking.price || 0)}
                statusKey={live.status}
                statusLabel={live.label}
                paymentNote={pk === 'paid' ? 'Fully paid' : undefined}
              />
            );
          })}
        </div>
      ) : (
        <EmptyState
          card
          title="No bookings for today"
          description={"Slots are open — start filling today's schedule."}
          action={
            <FreeSlotChip onClick={goBookNow}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Book a slot
            </FreeSlotChip>
          }
        />
      )}

      {/* Upcoming bookings */}
      <SectionHeader
        title="Upcoming bookings"
        to="/dashboard/my-bookings"
        trailingLink="View all"
      />
      {futureUpcoming.length > 0 ? (
        <div className="overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft">
          {futureUpcoming.map((booking, index) => {
            const cal = getBookingCalendarDateStr(booking);
            const when = cal ? format(parseISO(`${cal}T12:00:00`), 'MMM d') : '';
            const range = bookingTimeRange(booking);
            const timeLine = [when, range].filter(Boolean).join(' · ');
            const guest = booking.bookedBy || booking.teamA?.captainName || booking.contactName;
            const team = booking.teamA?.name;
            const title = guest && team && guest !== team
              ? `${guest} · ${team}`
              : bookingTitle(booking);
            return (
              <CompactBookingRow
                key={booking.id || `u-${index}`}
                timeLine={timeLine}
                title={title}
                amount={formatInr(booking.totalAmount || booking.price || 0)}
                statusKey={paymentKeyFor(booking)}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-ga-text-muted">No upcoming bookings.</p>
      )}

      {/* Alerts & tasks — compact lines */}
      <SectionHeader title="Alerts & tasks" />
      <div className="overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface px-4 py-3 shadow-ga-soft">
        {pendingCount > 0 && (
          <Link to="/dashboard/payments" className="block text-sm font-medium text-ga-warning">
            {pendingCount} payment{pendingCount === 1 ? '' : 's'} pending
            {pendingAmount > 0 ? ` · ${formatInr(pendingAmount)} due` : ''}
          </Link>
        )}
        {opsTotal > 0 && (
          <Link
            to="/dashboard/operations/daily"
            className={`block text-sm font-medium text-ga-success ${pendingCount > 0 ? 'mt-2' : ''}`}
          >
            Daily ops {opsDone}/{opsTotal} done
          </Link>
        )}
        {inventoryLowStockCount > 0 && (
          <Link
            to="/dashboard/assets/inventory?low_stock=true"
            className={`block text-sm font-medium text-ga-danger ${pendingCount > 0 || opsTotal > 0 ? 'mt-2' : ''}`}
          >
            {inventoryLowStockCount} inventory item{inventoryLowStockCount === 1 ? '' : 's'} low on stock
          </Link>
        )}
        {inventoryTotalItems > 0 && inventoryLowStockCount === 0 && (
          <Link
            to="/dashboard/assets/inventory"
            className={`block text-sm font-medium text-ga-text-primary ${pendingCount > 0 || opsTotal > 0 ? 'mt-2' : ''}`}
          >
            {inventoryTotalItems} inventory item{inventoryTotalItems === 1 ? '' : 's'} tracked
          </Link>
        )}
        {!hasAlerts && !hasInventory && (
          <p className="text-sm text-ga-text-muted">No alerts right now.</p>
        )}
      </div>

      {/* Insights & trends — compact lines */}
      <SectionHeader title="Insights & trends" />
      <div className="space-y-1.5 overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface px-4 py-3 text-sm shadow-ga-soft">
        <p className="text-ga-text-primary">
          <span className="text-ga-text-muted">Popular:</span>{' '}
          <span className="font-medium">{dynamicInsights.mostPopularService || '–'}</span>
        </p>
        <p className="text-ga-text-primary">
          <span className="text-ga-text-muted">Peak hours:</span>{' '}
          <span className="font-medium">{peakHours}</span>
        </p>
        <p className="text-ga-text-primary">
          <span className="text-ga-text-muted">Cancels:</span>{' '}
          <span className="font-medium">{dynamicInsights.cancellationsToday ?? 0}</span>
        </p>
      </div>
    </div>
  );
}
